import {
  Box,
  Divider,
  Flex,
  Image,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  Textarea,
  useColorMode
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useFilePicker } from 'use-file-picker';
import { ReactComponent as PlusIcon } from '../../icons/plus.svg';
import baseUrl from '@pocapan/shared/utils/baseUrl';
import getToken from '@pocapan/shared/utils/getToken';

interface DMUserProfile {
  userProfileId: string;
  nickname: string;
  identified: boolean;
  thumbnailUrl?: string;
  pictureUrl?: string;
}

interface DMMessageInputBoxProps {
  dmUserProfile?: DMUserProfile;
  messageAreaRef?: React.RefObject<HTMLDivElement>;
  onSentMessage: () => void;
  disabled: boolean;
}

async function sendMessage(chatRoomId: string, content: string, messageType: string, timestamp: number) {
  const url = `${baseUrl()}/chat_rooms/${chatRoomId}/chat_room_messages/`;
  const token = getToken();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        chat_room_message: {
          content: content,
          message_type: messageType,
          sent_at: timestamp
        }
      })
    });

    if (!response.ok) {
      const error = new Error(`HTTP error! Status: ${response.status}, URL: ${url}`);
      (error as any).responseStatus = response.status;
      throw error;
    }
    return response.json();
  } catch (error: any) {
    const shortenContent = content.length > 5 ? `${content.substring(0, 5)}...` : content;
    Sentry.captureException(error, {
      extra: {
        url,
        method: 'POST',
        chatRoomId,
        messageType,
        timestamp,
        content: shortenContent,
        token,
        responseStatus: error.responseStatus
      }
    });
    throw error;
  }
}

export default function DMMessageInputBox({
  dmUserProfile,
  messageAreaRef,
  onSentMessage,
  disabled
}: DMMessageInputBoxProps): JSX.Element {
  const { colorMode } = useColorMode();
  const params = useParams<{ chat_room_id: string }>();
  const [message, setMessage] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [sendingTimeout, setSendingTimeout] = useState<NodeJS.Timeout | null>(null);

  const requestSendMessage = async () => {
    if (isSending || disabled) return;

    setIsSending(true);

    // 전송 타임아웃 설정 (15초)
    const timeout = setTimeout(() => {
      setIsSending(false);
    }, 15000);
    setSendingTimeout(timeout);

    try {
      if (filesContent.length !== 0) {
        const response = await sendMessage(
          params.chat_room_id!,
          filesContent[0].content,
          'image',
          parseInt((Date.now() / 1000).toString())
        );
        clear();
        if (response) {
          onSentMessage();
        }
      } else if (!isEmpty(message)) {
        const response = await sendMessage(
          params.chat_room_id!,
          message,
          'text',
          parseInt((Date.now() / 1000).toString())
        );
        if (response) {
          onSentMessage();
        }
        setMessage('');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      if (sendingTimeout) {
        clearTimeout(sendingTimeout);
        setSendingTimeout(null);
      }
      setIsSending(false);
    }
  };

  const { openFilePicker, filesContent, clear } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false
  });

  // 컴포넌트 언마운트 시 타임아웃 cleanup
  React.useEffect(() => {
    return () => {
      if (sendingTimeout) {
        clearTimeout(sendingTimeout);
      }
    };
  }, [sendingTimeout]);

  const handleInputOnChanged = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
  };

  const handleFocusOnBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (e.relatedTarget === null) {
      e.target.focus();
    }
  };

  return (
    <Box
      className={colorMode === 'dark' ? 'darkBottomNavbar' : 'bottomNavbar'}
      paddingY={'20px'}
      paddingLeft={'20px'}
      maxW={'xl'}
      paddingTop={'0px'}
    >
      <Divider marginTop={'0px'} width={'calc(100% + 20px)'} marginLeft={'-20px'} />
      <Flex align={'center'} marginTop={'6px'} className="safeAreaMarginBottom">
        <Menu autoSelect={false} strategy={'fixed'}>
          <MenuButton>
            <Box className="bg-primary-light/15 dark:bg-primary-dark/15 rounded-lg mr-4 p-1.5 w-8 h-8">
              <PlusIcon
                width={'20px'}
                height={'20px'}
                className="fill-primary-light dark:fill-primary-dark"
              />
            </Box>
          </MenuButton>
          <MenuList>
            <MenuItem key={'image'} onClick={() => !disabled && openFilePicker()}>
              {t('chatRoom.imageMenu')}
            </MenuItem>
          </MenuList>
        </Menu>
        <div className="grow">
          {filesContent.length !== 0 ? (
            <Box borderWidth={'1px'} borderColor={'separator.light.2'} width={'fit-content'}>
              <Image width="120px" src={filesContent[0].content} />
            </Box>
          ) : (
            <Textarea
              className="!placeholder:text-placeholder-light dark:!placeholder:text-placeholder-dark"
              style={{
                height: '40px',
                minHeight: 'auto',
                resize: 'none'
              }}
              disabled={disabled}
              variant="unstyled"
              value={message}
              placeholder={disabled ? String(t('chatRoom.disabledPlaceHolder')) : String(t('chatRoom.placeHolder'))}
              onChange={handleInputOnChanged}
              onBlur={handleFocusOnBlur}
            />
          )}
        </div>
        {!isEmpty(message) || filesContent.length !== 0 ? (
          isSending ? (
            <Spinner
              size="sm"
              className="text-primary-light dark:text-primary-dark mr-5"
            />
          ) : (
            <Text
              className="!text-primary-light dark:!text-primary-dark cursor-pointer"
              minW={'28px'}
              fontSize={'15px'}
              fontWeight={600}
              marginRight={'20px'}
              onClick={() => requestSendMessage()}
            >
              {t('send')}
            </Text>
          )
        ) : (
          <></>
        )}
      </Flex>
    </Box>
  );
}
